import { inject, Injectable } from '@angular/core';
import { CheckoutValidator, ValidationErrorKey, ValidationErrors } from '@checkout-v2/const';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, skipWhile, switchMap, take, tap } from 'rxjs/operators';
import {
  CheckoutContactFormService,
  DeliveryAddressService,
  PaymentModesService,
  PickupService,
  ReceiptService,
  ServicesService,
  TimeSlotsService,
} from '../services';

export interface ValidatorWithErrors {
  validator: CheckoutValidator;
  errors: ValidationErrors | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class CheckoutValidationService {
  private readonly deliveryAddressService = inject(DeliveryAddressService);
  private readonly pickupService = inject(PickupService);
  private readonly servicesService = inject(ServicesService);
  private readonly timeSlotsService = inject(TimeSlotsService);
  private readonly paymentModesService = inject(PaymentModesService);
  private readonly checkoutContactFormService = inject(CheckoutContactFormService);
  private readonly receiptService = inject(ReceiptService);

  /**
   * Событие с ошибками валидации
   */
  private validationErrors$ = new BehaviorSubject<ValidationErrors>({});

  /**
   * Событие с ошибками валидации
   */
  private validationErrorsEvent$ = new Subject<ValidationErrors>();
  /**
   * Событие с ошибками к которым необходимо прокрутить страницу
   */
  private scrollToErrorsEvent$ = new Subject<ValidationErrors | undefined>();
  /**
   * При появлении ошибки валидации мы подписываемся на изменения
   * данных от которых зависит ошибка, чтобы когда изменится статус
   * валидации убрать ошибку. Тут хранятся ключи ошибок на которые
   * состоялась подписка.
   */
  private errorsDropQueue = new Set<ValidationErrorKey>();

  /**
   * Запуск валидации в чекауте
   * @returns ошибки валидации, если они есть
   */
  validate(): Observable<ValidationErrors | undefined> {
    return this.receiptService.getActiveReceiptTypeId().pipe(
      switchMap((receiptType) => {
        let validators = [
          this.resolveValidation(this.paymentModesService),
          this.resolveValidation(this.checkoutContactFormService),
        ];

        if (receiptType === 'delivery') {
          validators = [
            this.resolveValidation(this.deliveryAddressService),
            this.resolveValidation(this.servicesService),
            this.resolveValidation(this.timeSlotsService),
          ].concat(validators);
        }

        if (receiptType === 'pickup') {
          validators = [this.resolveValidation(this.pickupService)].concat(validators);
        }

        return combineLatest(validators);
      }),
      tap((result) => {
        let validationErrors: ValidationErrors = {};
        let scrollToErrors: ValidationErrors | undefined;
        const validatorsWithErrors: ValidatorWithErrors[] = [];

        for (const item of result) {
          if (!item.errors) {
            continue;
          }

          if (!scrollToErrors) {
            scrollToErrors = item.errors;
          }

          validationErrors = { ...validationErrors, ...item.errors };
          validatorsWithErrors.push(item);
        }

        this.validationErrors$.next(validationErrors);
        this.validationErrorsEvent$.next(validationErrors);
        this.scrollToErrorsEvent$.next(scrollToErrors);
        this.resetErrorsOnChange(validatorsWithErrors);
      }),
      map((result) => {
        const errors = result.reduce((acc, item) => ({ ...acc, ...item.errors }), {});

        return Object.keys(errors).length ? errors : undefined;
      }),
    );
  }

  /**
   * Подписка на события с ошибками
   * @param errorKeys список ключей ошибок. Если ничего не передать,
   * то будут приходить события со всеми ошибками.
   * @returns события с ошибками
   */
  getErrorsEvent(...errorKeys: ValidationErrorKey[]): Observable<ValidationErrors | undefined> {
    return this.validationErrorsEvent$.pipe(
      map((errors) => {
        let filteredErrors = errors;

        if (errorKeys.length) {
          filteredErrors = Object.fromEntries(
            Object.entries(errors).filter((e: [ValidationErrorKey, string]) => errorKeys.includes(e[0])),
          );
        }

        return Object.keys(filteredErrors).length > 0 ? filteredErrors : undefined;
      }),
    );
  }

  /**
   * Подписка на события скролла к ошибке. Если появляется ошибка и
   * к этой ошибке необходимо прокрутить страницу, то будет отправлено
   * событие скролла.
   * @param errorKeys список ключей ошибок.
   * @returns пустые события
   */
  getScrollToEvent(...errorKeys: ValidationErrorKey[]): Observable<void> {
    if (!errorKeys.length) {
      throw new Error('Аргумент errorKeys обязателен!');
    }

    return this.scrollToErrorsEvent$.pipe(
      filter((errors) => Object.keys(errors || {}).some((key: ValidationErrorKey) => errorKeys.includes(key))),
      map(() => undefined),
    );
  }

  /**
   * Сброс сообщений ошибок после смены статуса валидации
   */
  private resetErrorsOnChange(items: ValidatorWithErrors[]): void {
    items.forEach(({ errors, validator }) => {
      Object.keys(errors || {}).forEach((errorKey: ValidationErrorKey) => {
        if (this.errorsDropQueue.has(errorKey)) {
          return;
        }
        this.errorsDropQueue.add(errorKey);
        validator
          .getValidationErrors()
          .pipe(
            skipWhile((e) => Object.keys(e).length > 0),
            take(1),
          )
          .subscribe(() => {
            const validationErrors = this.validationErrors$.getValue();
            delete validationErrors[errorKey];
            this.validationErrors$.next(validationErrors);
            this.validationErrorsEvent$.next(validationErrors);
            this.errorsDropQueue.delete(errorKey);
          });
      });
    });
  }

  /**
   * Вызывает метод validate в переданном валидаторе
   * @returns статус валидации вместе с валидатором
   */
  private resolveValidation(validator: CheckoutValidator): Observable<ValidatorWithErrors> {
    return validator.getValidationErrors().pipe(
      take(1),
      map((errors) => ({
        errors: Object.keys(errors).length ? errors : undefined,
        validator,
      })),
    );
  }
}
